import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { TranslateModule } from "@ngx-translate/core";
import { ActionButtonComponent } from "../../buttons/action-button/action-button.component";
import { ActionButtonType } from "../../buttons/action-button/action-button.model";
import { PopUpInformalType } from "./pop-up-informal.model";
import { MatDialogRef } from "@angular/material/dialog";
import { PopUpConfirmComponent } from "../pop-up-confirm/pop-up-confirm.component";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { FormInputComponent } from "../../forms/form-input/form-input.component";
import { FormInputType } from "../../forms/form-input/form-input.model";

@Component({
  selector: "app-pop-up-informal",
  templateUrl: "./pop-up-informal.component.html",
  styleUrls: ["./pop-up-informal.component.scss"],
  imports: [
    TranslateModule,
    FontAwesomeModule,
    ActionButtonComponent,
    CommonModule,
    FormsModule,
    MatCheckboxModule,
    FormInputComponent,
  ],
  standalone: true,
})
export class PopUpInformalComponent implements OnInit, OnChanges, OnDestroy {
  private dialogRef = inject(MatDialogRef<PopUpConfirmComponent>);
  private autoCloseHandle: ReturnType<typeof setTimeout> | null = null;
  private autoCloseCountdownHandle: ReturnType<typeof setInterval> | null = null;
  @Input() title: string = "";
  @Input() message: string = "";
  @Input() subMessage: string = "";
  @Input() type: PopUpInformalType = PopUpInformalType.Info;
  @Input() showPrintReceipt: boolean = false;
  @Input() showSendReceiptByEmail: boolean = false;
  @Input() sendReceiptByEmail: boolean = true;
  @Input() emailAddress: string = "";
  @Input() confirmButtonText: string = "";
  @Input() cancelButtonText: string = "";
  @Input() showCancelButton: boolean = false;
  @Input() autoCloseMs: number | null = null;

  autoCloseSecondsRemaining: number | null = null;

  printReceipt: boolean = false;
  email: string = "";

  PopUpInformalType = PopUpInformalType;
  ActionButtonType = ActionButtonType;
  FormInputType = FormInputType;

  ngOnInit() {
    this.syncReceiptOptions();
    this.email = this.emailAddress;

    if (this.autoCloseMs && this.autoCloseMs > 0) {
      this.autoCloseSecondsRemaining = Math.max(Math.ceil(this.autoCloseMs / 1000), 1);
      this.autoCloseCountdownHandle = setInterval(() => {
        if (this.autoCloseSecondsRemaining === null) {
          return;
        }

        this.autoCloseSecondsRemaining -= 1;
        if (this.autoCloseSecondsRemaining <= 0) {
          this.autoCloseSecondsRemaining = 0;
          if (this.autoCloseCountdownHandle) {
            clearInterval(this.autoCloseCountdownHandle);
            this.autoCloseCountdownHandle = null;
          }
        }
      }, 1000);

      this.autoCloseHandle = setTimeout(() => this.onConfirm(), this.autoCloseMs);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['emailAddress']) {
      this.email = this.emailAddress;
    }

    if (changes['showPrintReceipt'] || changes['showSendReceiptByEmail']) {
      this.syncReceiptOptions();
    }
  }

  /**
   * Set email address from parent component
   * Called by PopUpDialogInformalService after dialog is opened
   * 
   * @param emailAddress - Email address to pre-fill
   */
  public setEmailAddress(emailAddress: string): void {
    this.emailAddress = emailAddress;
    this.email = emailAddress;
  }

  private syncReceiptOptions(): void {
    if (!this.showSendReceiptByEmail) {
      this.sendReceiptByEmail = false;
    }
  }

  ngOnDestroy(): void {
    if (this.autoCloseHandle) {
      clearTimeout(this.autoCloseHandle);
      this.autoCloseHandle = null;
    }

    if (this.autoCloseCountdownHandle) {
      clearInterval(this.autoCloseCountdownHandle);
      this.autoCloseCountdownHandle = null;
    }
  }

  onConfirm() {
    if (this.autoCloseHandle) {
      clearTimeout(this.autoCloseHandle);
      this.autoCloseHandle = null;
    }

    if (this.autoCloseCountdownHandle) {
      clearInterval(this.autoCloseCountdownHandle);
      this.autoCloseCountdownHandle = null;
    }

    // Validate email if sending receipt by email
    if (this.showSendReceiptByEmail && this.sendReceiptByEmail) {
      if (!this.email || !this.isValidEmail(this.email)) {
        return; // Don't close dialog if email is invalid
      }
    }

    if (this.dialogRef) {
      this.dialogRef.close({ 
        confirmed: true, 
        printReceipt: this.showPrintReceipt ? this.printReceipt : false,
        sendReceiptByEmail: this.showSendReceiptByEmail ? this.sendReceiptByEmail : false,
        email: this.sendReceiptByEmail ? this.email : ""
      });
    }
  }

  onCancel() {
    if (this.autoCloseHandle) {
      clearTimeout(this.autoCloseHandle);
      this.autoCloseHandle = null;
    }

    if (this.autoCloseCountdownHandle) {
      clearInterval(this.autoCloseCountdownHandle);
      this.autoCloseCountdownHandle = null;
    }

    if (this.dialogRef) {
      this.dialogRef.close({
        confirmed: false,
        printReceipt: false,
        sendReceiptByEmail: false,
        email: "",
      });
    }
  }

  /**
   * Validate email format
   * 
   * @param email - Email address to validate
   * @returns True if email is valid
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Check if confirm button should be disabled
   * Button is disabled when sendEmail is checked but email is invalid
   * 
   * @returns True if button should be disabled
   */
  isConfirmDisabled(): boolean {
    // Disable button if sending email is selected but email is not valid
    if (this.showSendReceiptByEmail && this.sendReceiptByEmail) {
      return !this.email || !this.isValidEmail(this.email);
    }
    return false;
  }

  get resolvedConfirmButtonText(): string {
    if (this.confirmButtonText) {
      return this.confirmButtonText;
    }

    return this.type === PopUpInformalType.Error ? "webPos.Ok" : "webPos.Done";
  }

  get resolvedCancelButtonText(): string {
    return this.cancelButtonText || "webPos.Cancel";
  }
}
